import { createContext, useContext } from "react";

export const NAV_BAR_HEIGHT = "5.5rem";
export const SIDE_BAR_WIDTH = "15.5rem";
export const TOOL_BAR_HEIGHT = "6.25rem";
export const FOOT_BAR_HEIGHT = "5rem";
export const STATUS_OPTIONS = [
  { status: "Never", color: "#F12727" },
  { status: "Cold", color: "#F18827" },
  { status: "Upcoming", color: "#FFFA74" },
  { status: "Warm", color: "#9747FF" },
  { status: "Almost", color: "#0A84FF" },
  { status: "Success", color: "#46BC4A" },
];
export const sortByOptions = ["Last Modified", "Alphabetical"];
export const ledUsageOptions = ["Rental", "Install", "Past"];
export const dmsOptions = [
  "AutoManager",
  "CDS",
  "CarSwap",
  "DealerSolutions",
  "EasyCars",
  "GumTree",
  "Other",
  "Pentana",
  "TradingPost",
  "Ultimate",
  "Unknown",
  "VirtualYard",
];
export const reminderValues = [
  "1 Hour",
  "1 Day",
  "2 Day",
  "3 Day",
  "1 Week",
  "1 Month",
  "Never",
];
export const BACKEND_URL = "/api";

export interface GlobalContextType {
  gridView: boolean;
  setGridView: (value: boolean) => void;
  selectedPageState: "viewAll" | "add" | "edit" | "viewOne" | "account";
  setSelectedPageState: (
    value: "viewAll" | "add" | "edit" | "viewOne" | "account"
  ) => void;
  businesses: Business[];
  loggedIn: LoggedInType;
  setLoggedIn: (value: LoggedInType) => void;
  selectedBusiness: string;
  setSelectedBusiness: (value: string) => void;
  selectedSortBy: string;
  setSelectedSortBy: (value: string) => void;
  formData: FormData;
  setFormData: (value: FormData) => void;
  linkedUsers: LinkedAccount[];
  moveBusiness: (
    draggedItem: {
      id: string;
      status: (typeof STATUS_OPTIONS)[number]["status"];
    },
    targetStatus: (typeof STATUS_OPTIONS)[number]["status"]
  ) => void;
  getLinks: () => void;
  getUserData: () => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  userData: UserData;
  setUserData: (value: UserData) => void;
  selectedStaffFilter: string[];
  setSelectedStaffFilter: (value: string[]) => void;
  filteredBusinesses: Business[];
  isMobile: boolean;
}

export const GlobalContext = createContext<GlobalContextType | undefined>(
  undefined
);

export type Business = {
  staff: string;
  rep: string;
  lastModified: Date;
  businessName: string;
  phone: string;
  state: string;
  status: (typeof STATUS_OPTIONS)[number]["status"];
  pitchCount: number;
};

export type Pitch = {
  id: number;
  staff: string;
  businessName: string;
  contactName: string;
  position:
    | "other"
    | "dealerPrincipal"
    | "generalManager"
    | "marketingManager"
    | "reception"
    | "salesManager";
  contactType: "review" | "phone" | "email" | "intro" | "follow" | "inPerson";
  phone: string;
  email: string;
  website: string;
  country: string;
  street: string;
  city: string;
  state: string;
  postCode: string;
  dms: string;
  ledUsage: string;
  status: (typeof STATUS_OPTIONS)[number]["status"];
  reminderDuration:
    | "1 Hour"
    | "1 Day"
    | "2 Day"
    | "3 Day"
    | "1 Week"
    | "1 Month"
    | "Never";
  contactSentiment: "positive" | "neutral" | "negative";
  note: string;
  lastModified: Date;
};

export type LoggedInType = {
  status: boolean;
  token: string;
};

export type FormData = {
  id: number;
  staff: string;
  businessName: string;
  contactName: string;
  position:
    | "other"
    | "dealerPrincipal"
    | "generalManager"
    | "marketingManager"
    | "reception"
    | "salesManager";
  contactType: "review" | "phone" | "email" | "intro" | "follow" | "inPerson";
  phone: string;
  email: string;
  website: string;
  country: string;
  street: string;
  city: string;
  state: string;
  postCode: string;
  dms: string;
  ledUsage: string;
  status: (typeof STATUS_OPTIONS)[number]["status"];
  reminderDuration:
    | "1 Hour"
    | "1 Day"
    | "2 Day"
    | "3 Day"
    | "1 Week"
    | "1 Month"
    | "Never";
  contactSentiment: "positive" | "neutral" | "negative";
  note: string;
};

export interface LinkedAccount {
  name: string;
  email: string;
  status: "pending" | "accepted" | "rejected";
}

export type UserData = {
  name: string;
  email: string;
  business: string;
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  }
  return context;
};
